import { useEffect, useState } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import EditButton from '../components/EditButtonModal'
import DeleteButton from '../components/DeleteButtonModal'

const emptyProvider = {
  name: '',
  country_code: '',
  website_url: '',
  street: '',
  city: '',
  psc: '',
  is_active: true,
  sort_order: 0
}

const emptyContact = {
  insurance_provider_id: '',
  type: '',
  label: '',
  info_phone: '',
  assist_phone: '',
  info_phone_abroad: '',
  assist_phone_abroad: '',
  email: '',
  form_url: '',
  note: '',
  is_24_7: false
}

const AdminInsurance = () => {
  const [providers, setProviders] = useState([])
  const [expandedProviderId, setExpandedProviderId] = useState(null)

  const [providerFormVisible, setProviderFormVisible] = useState(false)
  const [contactFormVisible, setContactFormVisible] = useState(false)

  const [providerForm, setProviderForm] = useState(emptyProvider)
  const [contactForm, setContactForm] = useState(emptyContact)

  const [editProviderId, setEditProviderId] = useState(null)
  const [editProviderForm, setEditProviderForm] = useState(emptyProvider)

  const [editContactId, setEditContactId] = useState(null)
  const [editContactForm, setEditContactForm] = useState(emptyContact)

  const loadProviders = async () => {
    const res = await fetchWithAuth('/insurance')
    const baseProviders = await res.json()

    const providersWithContacts = await Promise.all(
      baseProviders.map(async (p) => {
        const r = await fetchWithAuth(`/insurance/${p.id}`)
        const data = await r.json()

        return {
          ...p,
          contacts: data.contacts || []
        }
      })
    )

    setProviders(providersWithContacts)
  }

  useEffect(() => {
    loadProviders()
  }, [])

  const saveProvider = async () => {
    const res = await fetchWithAuth(
      '/admin/insurance/providers',
      {
        method: 'POST',
        body: JSON.stringify(providerForm)
      }
    )

    if (!res.ok) {
      alert('Chyba pri ukladaní poisťovne')
      return
    }

    setProviderForm(emptyProvider)
    setProviderFormVisible(false)
    loadProviders()
  }

  const editProvider = (p) => {
    setEditProviderId(p.id)

    setEditProviderForm({
      name: p.name,
      country_code: p.country_code,
      website_url: p.website_url,
      street: p.street || '',
      city: p.city || '',
      psc: p.psc || '',
      is_active: p.is_active,
      sort_order: p.sort_order
    })
  }

  const saveProviderEdit = async (id) => {
    const res = await fetchWithAuth(
      `/admin/insurance/providers/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(editProviderForm)
      }
    )

    if (!res.ok) {
      alert('Chyba pri ukladaní poisťovne')
      return
    }

    setEditProviderId(null)
    setEditProviderForm(emptyProvider)
    loadProviders()
  }

  const cancelProviderEdit = () => {
    setEditProviderId(null)
    setEditProviderForm(emptyProvider)
  }

  const deleteProvider = async (id) => {
    if (!window.confirm('Zmazať poisťovňu?')) return

    await fetchWithAuth(
      `/admin/insurance/providers/${id}`,
      {
        method: 'DELETE'
      }
    )

    loadProviders()
  }

  const saveContact = async () => {
    if (!contactForm.insurance_provider_id) {
      alert('Vyber poisťovňu')
      return
    }

    const providerId = contactForm.insurance_provider_id

    const res = await fetchWithAuth(
      '/admin/insurance/contacts',
      {
        method: 'POST',
        body: JSON.stringify(contactForm)
      }
    )

    if (!res.ok) {
      alert('Chyba pri ukladaní kontaktu')
      return
    }

    setContactForm(emptyContact)
    setContactFormVisible(false)

    loadProviders()
    setExpandedProviderId(providerId)
  }

  const editContact = (c) => {
    setEditContactId(c.id)

    setEditContactForm({
      insurance_provider_id: c.insurance_provider_id,
      type: c.type || '',
      label: c.label || '',
      info_phone: c.info_phone || '',
      assist_phone: c.assist_phone || '',
      info_phone_abroad: c.info_phone_abroad || '',
      assist_phone_abroad: c.assist_phone_abroad || '',
      email: c.email || '',
      form_url: c.form_url || '',
      note: c.note || '',
      is_24_7: c.is_24_7
    })
  }

  const saveContactEdit = async (id, providerId) => {
    const res = await fetchWithAuth(
      `/admin/insurance/contacts/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(editContactForm)
      }
    )

    if (!res.ok) {
      alert('Chyba pri ukladaní kontaktu')
      return
    }

    setEditContactId(null)
    setEditContactForm(emptyContact)

    loadProviders()
    setExpandedProviderId(providerId)
  }

  const cancelContactEdit = () => {
    setEditContactId(null)
    setEditContactForm(emptyContact)
  }

  const deleteContact = async (id, providerId) => {
    if (!window.confirm('Zmazať kontakt?')) return

    await fetchWithAuth(
      `/admin/insurance/contacts/${id}`,
      {
        method: 'DELETE'
      }
    )

    loadProviders()
    setExpandedProviderId(providerId)
  }

  const input = (
    label,
    key,
    formType = 'provider'
  ) => (
    <div style={{ marginBottom: 6 }}>
      <label>
        <strong>{label}</strong>
        <br />

        <input
          type="text"
          value={
            formType === 'provider'
              ? providerForm[key]
              : contactForm[key]
          }
          onChange={(e) =>
            formType === 'provider'
              ? setProviderForm({
                  ...providerForm,
                  [key]: e.target.value
                })
              : setContactForm({
                  ...contactForm,
                  [key]: e.target.value
                })
          }
          style={{ width: 220 }}
        />
      </label>
    </div>
  )

  const providerEditInput = (key) => (
    <input
      type="text"
      value={editProviderForm[key]}
      onChange={(e) =>
        setEditProviderForm({
          ...editProviderForm,
          [key]: e.target.value
        })
      }
    />
  )

  const contactEditInput = (key) => (
    <input
      type="text"
      value={editContactForm[key]}
      onChange={(e) =>
        setEditContactForm({
          ...editContactForm,
          [key]: e.target.value
        })
      }
    />
  )

  return (
    <div
      style={{
        background: '#ededed',
        padding: 12,
        border: '1.5px solid #107c6b',
        borderRadius: 10,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <h3
        style={{
          fontSize: 18,
          marginTop: 0,
          marginBottom: 10
        }}
      >
        Správa poisťovní a kontaktov
      </h3>

      {!providerFormVisible && (
        <button
          onClick={() => {
            setProviderForm(emptyProvider)
            setProviderFormVisible(true)
          }}
          style={{
            alignSelf: 'flex-start'
          }}
        >
          Pridať poisťovňu
        </button>
      )}

      {providerFormVisible && (
        <div
          style={{
            marginTop: 20,
            padding: 10,
            border: '1px solid #ccc',
            borderRadius: 10,
            background: '#fff'
          }}
        >
          <h3>
            Pridať poisťovňu
          </h3>

          <div
            style={{
              display: 'flex',
              gap: 20,
              flexWrap: 'wrap'
            }}
          >
            {input('Názov', 'name')}
            {input('Country code', 'country_code')}
            {input('Web URL', 'website_url')}
            {input('Ulica', 'street')}
            {input('Mesto', 'city')}
            {input('PSC', 'psc')}
            {input('Sort order', 'sort_order')}

            <div>
              <label>
                <strong>Aktívna</strong>
                <br />

                <select
                  value={
                    providerForm.is_active
                      ? '1'
                      : '0'
                  }
                  onChange={(e) =>
                    setProviderForm({
                      ...providerForm,
                      is_active:
                        e.target.value === '1'
                    })
                  }
                  style={{ width: 220 }}
                >
                  <option value="1">
                    Áno
                  </option>

                  <option value="0">
                    Nie
                  </option>
                </select>
              </label>
            </div>
          </div>

          <button
            onClick={saveProvider}
            style={{ marginTop: 10 }}
          >
            Pridať
          </button>

          <button
            onClick={() => {
              setProviderFormVisible(false)
              setProviderForm(emptyProvider)
            }}
            style={{ marginLeft: 10 }}
          >
            Zrušiť
          </button>
        </div>
      )}

      <hr
        style={{
          width: '100%'
        }}
      />

      <div
        style={{
          border: '1px solid #c7c7c7',
          borderRadius: 10,
          background: '#fff',
          flex: 1,
          minHeight: 0,
          maxWidth: '100%',
          overflow: 'auto'
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}
        >
          <thead>
            <tr
              style={{
                background: '#c8e1e1'
              }}
            >
              <th style={headerCell}>ID</th>
              <th style={headerCell}>Názov</th>
              <th style={headerCell}>Country</th>
              <th style={headerCell}>Web</th>
              <th style={headerCell}>Ulica</th>
              <th style={headerCell}>Mesto</th>
              <th style={headerCell}>PSC</th>
              <th style={headerCell}>Aktívna</th>
              <th style={headerCell}>Sort</th>
              <th style={headerCell}>Verified</th>
              <th style={headerCell}>Kontakty</th>
              <th style={headerCell}>Akcie</th>
            </tr>
          </thead>

          <tbody>
            {providers.map((p) => (
              <>
                <tr key={p.id}>
                  <td style={bodyCell}>
                    {p.id}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('name')
                      : p.name}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('country_code')
                      : p.country_code}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('website_url')
                      : p.website_url}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('street')
                      : p.street}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('city')
                      : p.city}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('psc')
                      : p.psc}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id ? (
                      <select
                        value={
                          editProviderForm.is_active
                            ? '1'
                            : '0'
                        }
                        onChange={(e) =>
                          setEditProviderForm({
                            ...editProviderForm,
                            is_active:
                              e.target.value === '1'
                          })
                        }
                      >
                        <option value="1">
                          Áno
                        </option>

                        <option value="0">
                          Nie
                        </option>
                      </select>
                    ) : (
                      p.is_active
                        ? 'Áno'
                        : 'Nie'
                    )}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id
                      ? providerEditInput('sort_order')
                      : p.sort_order}
                  </td>

                  <td style={bodyCell}>
                    {p.last_verified_at ?? '-'}
                  </td>

                  <td style={bodyCell}>
                    {p.contacts.length}
                  </td>

                  <td style={bodyCell}>
                    {editProviderId === p.id ? (
                      <>
                        <button
                          onClick={() =>
                            saveProviderEdit(p.id)
                          }
                        >
                          Uložiť
                        </button>

                        <button
                          onClick={cancelProviderEdit}
                          style={{ marginLeft: 10 }}
                        >
                          Zrušiť
                        </button>
                      </>
                    ) : (
                      <>
                        {p.contacts.length > 0 ? (
                          <button
                            onClick={() =>
                              setExpandedProviderId(
                                expandedProviderId === p.id
                                  ? null
                                  : p.id
                              )
                            }
                          >
                            {expandedProviderId === p.id
                              ? 'Skryť kontakty'
                              : 'Kontakty'}
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setExpandedProviderId(p.id)
                              setContactFormVisible(false)
                            }}
                          >
                            Pridať kontakt
                          </button>
                        )}

<EditButton
  onClick={() =>
    editProvider(p)
  }
/>

<DeleteButton
  onClick={() =>
    deleteProvider(p.id)
  }
/>
                      </>
                    )}
                  </td>
                </tr>

                {expandedProviderId === p.id && (
                  <tr>
                    <td
                      colSpan="12"
                      style={{
                        background: '#f2f2f2',
                        padding: 12
                      }}
                    >
                      <h4>
                        Kontakty poisťovne: {p.name}
                      </h4>

                      {p.contacts.length > 0 && (
                        <div
                          style={{
                            border: '1px solid #c7c7c7',
                            borderRadius: 10,
                            overflow: 'auto',
                            background: '#fff'
                          }}
                        >
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse'
                            }}
                          >
                            <thead>
                              <tr
                                style={{
                                  background: '#c8e1e1'
                                }}
                              >
                                <th style={headerCell}>ID</th>
                                <th style={headerCell}>Typ</th>
                                <th style={headerCell}>Popis</th>
                                <th style={headerCell}>Info tel</th>
                                <th style={headerCell}>Asist tel</th>
                                <th style={headerCell}>Info tel zahr.</th>
                                <th style={headerCell}>Asist tel zahr.</th>
                                <th style={headerCell}>Email</th>
                                <th style={headerCell}>Form URL</th>
                                <th style={headerCell}>Poznámka</th>
                                <th style={headerCell}>24/7</th>
                                <th style={headerCell}>Akcie</th>
                              </tr>
                            </thead>

                            <tbody>
                              {p.contacts.map((c) => (
                                <tr key={c.id}>
                                  <td style={bodyCell}>
                                    {c.id}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('type')
                                      : c.type}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('label')
                                      : c.label}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('info_phone')
                                      : c.info_phone}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('assist_phone')
                                      : c.assist_phone}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('info_phone_abroad')
                                      : c.info_phone_abroad}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('assist_phone_abroad')
                                      : c.assist_phone_abroad}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('email')
                                      : c.email}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('form_url')
                                      : c.form_url}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id
                                      ? contactEditInput('note')
                                      : c.note}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id ? (
                                      <select
                                        value={
                                          editContactForm.is_24_7
                                            ? '1'
                                            : '0'
                                        }
                                        onChange={(e) =>
                                          setEditContactForm({
                                            ...editContactForm,
                                            is_24_7:
                                              e.target.value === '1'
                                          })
                                        }
                                      >
                                        <option value="1">
                                          Áno
                                        </option>

                                        <option value="0">
                                          Nie
                                        </option>
                                      </select>
                                    ) : (
                                      c.is_24_7
                                        ? 'Áno'
                                        : 'Nie'
                                    )}
                                  </td>

                                  <td style={bodyCell}>
                                    {editContactId === c.id ? (
                                      <>
                                        <button
                                          onClick={() =>
                                            saveContactEdit(
                                              c.id,
                                              p.id
                                            )
                                          }
                                        >
                                          Uložiť
                                        </button>

                                        <button
                                          onClick={cancelContactEdit}
                                          style={{
                                            marginLeft: 10
                                          }}
                                        >
                                          Zrušiť
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() =>
                                            editContact(c)
                                          }
                                        >
                                          Upraviť
                                        </button>

                                        <button
                                          onClick={() =>
                                            deleteContact(
                                              c.id,
                                              p.id
                                            )
                                          }
                                          style={{
                                            marginLeft: 10
                                          }}
                                        >
                                          Zmazať
                                        </button>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {!contactFormVisible && (
                        <button
                          style={{ marginTop: 15 }}
                          onClick={() => {
                            setContactForm({
                              ...emptyContact,
                              insurance_provider_id: p.id
                            })

                            setContactFormVisible(true)
                          }}
                        >
                          Pridať kontakt
                        </button>
                      )}

                      {contactFormVisible && (
                        <div
                          style={{
                            marginTop: 20,
                            padding: 10,
                            border: '1px solid #ccc',
                            borderRadius: 10,
                            background: '#fff'
                          }}
                        >
                          <h4>
                            Pridať kontakt
                          </h4>

                          <div
                            style={{
                              display: 'flex',
                              gap: 20,
                              flexWrap: 'wrap'
                            }}
                          >
                            {input(
                              'Typ',
                              'type',
                              'contact'
                            )}

                            {input(
                              'Popis',
                              'label',
                              'contact'
                            )}

                            {input(
                              'Info telefón',
                              'info_phone',
                              'contact'
                            )}

                            {input(
                              'Asist telefón',
                              'assist_phone',
                              'contact'
                            )}

                            {input(
                              'Info tel. zahr.',
                              'info_phone_abroad',
                              'contact'
                            )}

                            {input(
                              'Asist tel. zahr.',
                              'assist_phone_abroad',
                              'contact'
                            )}

                            {input(
                              'Email',
                              'email',
                              'contact'
                            )}

                            {input(
                              'Form URL',
                              'form_url',
                              'contact'
                            )}

                            {input(
                              'Poznámka',
                              'note',
                              'contact'
                            )}

                            <div>
                              <label>
                                <strong>24/7</strong>
                                <br />

                                <select
                                  value={
                                    contactForm.is_24_7
                                      ? '1'
                                      : '0'
                                  }
                                  onChange={(e) =>
                                    setContactForm({
                                      ...contactForm,
                                      is_24_7:
                                        e.target.value === '1'
                                    })
                                  }
                                  style={{
                                    width: 220
                                  }}
                                >
                                  <option value="1">
                                    Áno
                                  </option>

                                  <option value="0">
                                    Nie
                                  </option>
                                </select>
                              </label>
                            </div>
                          </div>

                          <button
                            onClick={saveContact}
                          >
                            Pridať kontakt
                          </button>

                          <button
                            onClick={() => {
                              setContactFormVisible(false)
                              setContactForm(emptyContact)
                            }}
                            style={{
                              marginLeft: 10
                            }}
                          >
                            Zrušiť
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminInsurance

const headerCell = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 700,
  fontSize: 13
}

const bodyCell = {
  textAlign: 'left',
  padding: '10px 12px',
  borderTop: '1px solid #ececec',
  fontSize: 13
}